/**
 * Battle input model shared by the game, the replay system and the multiplayer backend.
 *
 * The original simulation consumes, every tick of `proc.ongoing()` (proc.as:322-420), the pair
 * `{ space: FWP.SPACE_PRESSED, other: FWP.KEY_PRESSED }` plus two "call" actions that bypass the keyboard:
 * a weapon choice from the weapon sheet (`call_weapon`) and a click target (`call_click`).
 * The replay recorder (`proc.write_key_press`, proc.as:3000) serializes exactly this information, so an
 * `InputFrame` is a lossless typed view of one recorded tick.
 */

/** Values of `FWP.KEY_PRESSED` (FWP.as:16467-16552) plus the recorder-only pseudo keys `weapon` and `click`. */
export const KEY_OTHER_VALUES = [
  'none',
  '1',
  '2',
  '3',
  '4',
  '5',
  '-',
  '+',
  'up',
  'down',
  'left',
  'right',
  'up+left',
  'up+right',
  'down+left',
  'down+right',
  'enter',
  'backspace',
  'escape',
  'weapon',
  'click',
] as const;

export type KeyOther = (typeof KEY_OTHER_VALUES)[number];

const KEY_OTHER_SET: ReadonlySet<string> = new Set(KEY_OTHER_VALUES);

export function isKeyOther(value: unknown): value is KeyOther {
  return typeof value === 'string' && KEY_OTHER_SET.has(value);
}

/** A real keyboard key: a `KeyOther` value other than 'none' and the pseudo keys 'weapon' / 'click'. */
export type HeldKey = Exclude<KeyOther, 'none' | 'weapon' | 'click'>;

export function isHeldKey(value: unknown): value is HeldKey {
  return isKeyOther(value) && value !== 'none' && value !== 'weapon' && value !== 'click';
}

/**
 * One tick of player input.
 *
 * `space`/`other` are the keys the worms receive (proc.ongoing hands `{space, other}` to obj.ongoing only while the
 * player controls the worm, the same gating the legacy recorder applies). The battle input seam
 * (src/game/runtime/battle-input.ts) adds what the legacy format cannot express, so that a re-simulation is exact:
 *  - `key`: a key the simulation read although the worms did not get it (proc.choosing_worm and
 *    proc.check_mousecontrol read the keyboard on every tick, e.g. on the tick of a weapon choice or a map click,
 *    where `other` is the pseudo key);
 *  - `scanner`: uses of the enemy scanner (new_interf.open_scaner spends one "ultra_scaner") before this tick;
 *  - `surrender`: the player gave the battle up (proc.cancel_fight) before this tick: the battle ends there.
 * `weapon`/`click`, `scanner` and `surrender` are one-shot events: their commands never repeat.
 */
export interface InputFrame {
  space: boolean;
  other: KeyOther;
  /** Weapon id (WEAPONS_DEF[*].id) when `other === 'weapon'`. */
  weapon?: number;
  /** Weapon range/level (1..10) when `other === 'weapon'`. */
  range?: number;
  /** Click target in world coordinates when `other === 'click'`. */
  clickX?: number;
  clickY?: number;
  /** Keyboard key read by the simulation on this tick when it differs from `other` (absent = `other`'s key or none). */
  key?: HeldKey;
  /** Scanner uses before this tick (1..). */
  scanner?: number;
  /** The player surrendered before this tick. */
  surrender?: true;
}

/**
 * A run-length encoded input record: `frame` is held at `tick` and for `repeat` further consecutive ticks.
 * This is the typed form of one legacy replay record `<tick>&<code>[%<repeat>]$` (proc.as:3037-3054).
 * One-shot frames (weapon, click, scanner, surrender; see `isOneShotFrame`) always have `repeat === 0`.
 */
export interface InputCommand {
  tick: number;
  frame: InputFrame;
  repeat: number;
}

export const EMPTY_FRAME: Readonly<InputFrame> = Object.freeze({ space: false, other: 'none' as KeyOther });

export function isEmptyFrame(frame: InputFrame): boolean {
  return !frame.space && frame.other === 'none' && !frame.key && !frame.scanner && !frame.surrender;
}

/** A frame that happens once (a weapon choice, a map click, a scanner use, a surrender): never repeated. */
export function isOneShotFrame(frame: InputFrame): boolean {
  return frame.other === 'weapon' || frame.other === 'click' || !!frame.scanner || !!frame.surrender;
}

/** The keyboard key the simulation reads on a tick of `frame` (proc.choosing_worm, proc.check_mousecontrol). */
export function frameKey(frame: InputFrame): KeyOther {
  if (frame.key) return frame.key;
  return frame.other === 'weapon' || frame.other === 'click' ? 'none' : frame.other;
}

export function framesEqual(a: InputFrame, b: InputFrame): boolean {
  return (
    a.space === b.space &&
    a.other === b.other &&
    (a.weapon ?? 0) === (b.weapon ?? 0) &&
    (a.range ?? 0) === (b.range ?? 0) &&
    (a.clickX ?? 0) === (b.clickX ?? 0) &&
    (a.clickY ?? 0) === (b.clickY ?? 0) &&
    (a.key ?? 'none') === (b.key ?? 'none') &&
    (a.scanner ?? 0) === (b.scanner ?? 0) &&
    !!a.surrender === !!b.surrender
  );
}

export function cloneFrame(frame: InputFrame): InputFrame {
  const out: InputFrame = { space: frame.space, other: frame.other };
  if (frame.other === 'weapon') {
    out.weapon = frame.weapon ?? 0;
    out.range = frame.range ?? 0;
  } else if (frame.other === 'click') {
    out.clickX = frame.clickX ?? 0;
    out.clickY = frame.clickY ?? 0;
  }
  if (frame.key) out.key = frame.key;
  if (frame.scanner) out.scanner = frame.scanner;
  if (frame.surrender) out.surrender = true;
  return out;
}

/** Key letter per `other` value, as in `FWP.get_char_from_key` (FWP.as:21090-21160). */
const KEY_TO_CHAR: Readonly<Record<Exclude<KeyOther, 'none'>, string>> = {
  '1': 'A',
  '2': 'B',
  '3': 'C',
  '4': 'D',
  '5': 'E',
  '-': 'F',
  '+': 'G',
  up: 'H',
  down: 'I',
  left: 'J',
  right: 'K',
  'up+left': 'L',
  'up+right': 'M',
  'down+left': 'N',
  'down+right': 'O',
  enter: 'P',
  backspace: 'Q',
  escape: 'R',
  weapon: 'S',
  click: 'T',
};

const CHAR_TO_KEY: ReadonlyMap<string, KeyOther> = new Map(
  (Object.entries(KEY_TO_CHAR) as [KeyOther, string][]).map(([k, c]) => [c, k]),
);

/**
 * `FWP.get_char_from_key`: the legacy key code of a frame. `''` for no key; a leading `U` marks SPACE.
 * Unknown `other` values (the game passes `''` when it has no control) map to no key, as in AS3.
 */
export function frameToLegacyCode(frame: { space: boolean; other: string }): string {
  const ch = frame.other in KEY_TO_CHAR ? KEY_TO_CHAR[frame.other as Exclude<KeyOther, 'none'>] : '';
  return frame.space ? 'U' + ch : ch;
}

/**
 * `FWP.get_key_from_char` (FWP.as:21163-21250), including its quirk: any code longer than one character is
 * treated as "SPACE + second character"; a lone `U` is SPACE only.
 */
export function legacyCodeToFrame(code: string): InputFrame {
  const frame: InputFrame = { space: false, other: 'none' };
  if (code.length === 0) return frame;
  let ch: string;
  if (code.length > 1) {
    frame.space = true;
    ch = code.charAt(1);
  } else {
    ch = code.charAt(0);
  }
  if (ch === 'U') {
    frame.space = true;
    return frame;
  }
  const key = CHAR_TO_KEY.get(ch);
  if (key) frame.other = key;
  return frame;
}
